import * as yup from 'yup';
import { Local } from '@/entities/auth/local.enum';
import { ageVerification } from './validation-types/age-verification';
import { type MaybeRefOrGetter, toRef } from '@vueuse/core';
import { Gender } from '@/entities/auth/gender.enum';
import { normalizeVatId } from '@/utils/vat-id';
import { vatIdSchema } from './vat-id.schema';
import { TaxProcedure } from '@carvia/ros-client-types';

const nullToUndefined = (value: unknown) =>
  value === null || value === undefined ? undefined : value;

const optionalString = () => yup.string().transform(nullToUndefined).optional();

const vatIdField = (taxProcedure?: MaybeRefOrGetter<TaxProcedure | undefined>) =>
  vatIdSchema()
    .test('reverseChargeRequiresVatId', 'VAT ID is required for reverse charge', function (value) {
      const procedure = taxProcedure ? toRef(taxProcedure).value : undefined;
      if (procedure === TaxProcedure.REVERSE_CHARGE) {
        return !!normalizeVatId(value);
      }
      return true;
    })
    .optional();

export const customerSchema = (
  minAge: MaybeRefOrGetter<number>,
  bookingStart: MaybeRefOrGetter<string | Date>,
  taxProcedure?: MaybeRefOrGetter<TaxProcedure | undefined>,
) => {
  return {
    company: optionalString(),
    gender: yup.string().transform(nullToUndefined).oneOf(Object.values(Gender)).optional(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    phone: yup.string().transform(nullToUndefined).required(),
    street: yup.string().required(),
    zip: yup.string().required(),
    city: yup.string().required(),
    country: yup.string().required(),
    dateOfBirth: ageVerification(minAge, bookingStart),
    preferredLocal: yup.string().oneOf(Object.values(Local)).required(),
    vatId: vatIdField(taxProcedure),
  };
};

export const customerOptionalSchema = (
  minAge: MaybeRefOrGetter<number>,
  bookingStart: MaybeRefOrGetter<string | Date>,
  taxProcedure?: MaybeRefOrGetter<TaxProcedure | undefined>,
) => {
  return {
    company: optionalString(),
    gender: yup.string().transform(nullToUndefined).oneOf(Object.values(Gender)).required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    phone: optionalString(),
    street: optionalString(),
    zip: optionalString(),
    city: optionalString(),
    country: optionalString(),
    dateOfBirth: ageVerification(minAge, bookingStart, true),
    preferredLocal: yup.string().oneOf(Object.values(Local)).required(),
    vatId: vatIdField(taxProcedure),
  };
};

const emailFieldValidation = (isRequired: MaybeRefOrGetter<boolean>, fieldName: string) =>
  yup
    .string()
    .transform((value) => {
      const trimmed = typeof value === 'string' ? value.trim() : value;
      return trimmed || undefined;
    })
    .email()
    .test(`require${fieldName}IfRequired`, `${fieldName} is a required field`, (val) => {
      return toRef(isRequired).value ? !!val : true;
    });

export const customerContactEmailSchema = (isRequired: MaybeRefOrGetter<boolean> = true) => ({
  contactEmail: emailFieldValidation(isRequired, 'contactEmail'),
});

export const customerEmailSchema = (isRequired: MaybeRefOrGetter<boolean> = true) => ({
  email: emailFieldValidation(isRequired, 'email'),
});

export const customerDocumentsSchema = {
  licenseNumber: yup.string().optional(),
  licenseDate: yup.date().optional(),
  licenseAuthority: yup.string().optional(),
  idCardNumber: yup.string().optional(),
  idCardDate: yup.date().optional(),
  idCardAuthority: yup.string().optional(),
};

export const customerEditSchema = (
  minAge: MaybeRefOrGetter<number>,
  bookingStart: MaybeRefOrGetter<string | Date>,
  contactEmailRequired: MaybeRefOrGetter<boolean> = false,
  emailRequired: MaybeRefOrGetter<boolean> = false,
  taxProcedure?: MaybeRefOrGetter<TaxProcedure | undefined>,
) => {
  return yup.object({
    ...customerOptionalSchema(minAge, bookingStart, taxProcedure),
    ...customerContactEmailSchema(contactEmailRequired),
    ...customerEmailSchema(emailRequired),
  });
};

export const customerHandoverSchema = (
  minAge: MaybeRefOrGetter<number>,
  bookingStart: MaybeRefOrGetter<string | Date>,
  contactEmailRequired: MaybeRefOrGetter<boolean> = true,
  phoneRequired: MaybeRefOrGetter<boolean> = true,
) => {
  const baseSchema = customerSchema(minAge, bookingStart);
  return yup.object({
    ...baseSchema,
    phone: yup.string().test('requirePhoneIfRequired', 'phone is a required field', (val) => {
      return toRef(phoneRequired).value ? !!val : true;
    }),
    ...customerContactEmailSchema(contactEmailRequired),
    ...customerDocumentsSchema,
  });
};
